import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { APP_TOKEN_KEY } from "./appAuthSlice";

const activityApi = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

activityApi.interceptors.request.use((config) => {
  const token = localStorage.getItem(APP_TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const fetchUserActivities = createAsyncThunk(
  "activities/fetchUserActivities",
  async ({ userId, page = 1, limit = 25, isRead = null, action = null }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      params.append("page", String(page));
      params.append("limit", String(limit));
      if (isRead !== null && typeof isRead !== "undefined") {
        params.append("isRead", String(isRead));
      }
      if (action) params.append("action", action);

      const response = await activityApi.get(`/api/activities/user/${userId}?${params.toString()}`);
      const payload = response.data || {};
      return {
        activities: payload.data || [],
        currentPage: payload.page || page,
        totalPages: payload.totalPages || 0,
        total: payload.total || 0,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching activities");
    }
  }
);

export const deleteActivityById = createAsyncThunk(
  "activities/deleteActivityById",
  async (activityId, { rejectWithValue }) => {
    try {
      await activityApi.delete(`/api/activities/${activityId}`);
      return activityId;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error deleting activity");
    }
  }
);

export const fetchActivitySummary = createAsyncThunk(
  "activities/fetchActivitySummary",
  async (_, { rejectWithValue }) => {
    try {
      const response = await activityApi.get("/api/activities/me/summary");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching summary");
    }
  }
);

export const markActivityRead = createAsyncThunk(
  "activities/markActivityRead",
  async (activityId, { rejectWithValue }) => {
    try {
      await activityApi.patch(`/api/activities/${activityId}/read`);
      return activityId;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error marking read");
    }
  }
);

export const markAllActivitiesRead = createAsyncThunk(
  "activities/markAllActivitiesRead",
  async (_, { rejectWithValue }) => {
    try {
      await activityApi.post("/api/activities/me/mark-all-read");
      return true;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error marking all read");
    }
  }
);

const initialState = {
  activities: [],
  pagination: {
    currentPage: 1,
    totalPages: 0,
    total: 0,
    hasNext: false,
    hasPrev: false,
  },
  loading: false,
  error: null,
  unreadCount: 0,
  summaryPreview: [],
  summaryLoading: false,
  markAllLoading: false,
};

const activitySlice = createSlice({
  name: "activities",
  initialState,
  reducers: {
    clearActivities(state) {
      state.activities = [];
      state.pagination = initialState.pagination;
      state.loading = false;
      state.error = null;
      state.unreadCount = 0;
      state.summaryPreview = [];
    },
    prependActivity(state, action) {
      const row = action.payload;
      if (!row?._id) return;
      const exists = state.activities.some((a) => String(a._id) === String(row._id));
      if (exists) return;
      state.activities = [row, ...state.activities];
      state.pagination.total = (state.pagination.total || 0) + 1;
      if (!row.isRead) {
        state.unreadCount = Math.max(0, state.unreadCount + 1);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserActivities.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserActivities.fulfilled, (state, action) => {
        state.loading = false;
        const { activities, totalPages, currentPage, total } = action.payload;

        if (currentPage === 1) {
          state.activities = activities;
        } else {
          const seen = new Set(state.activities.map((a) => a._id));
          const merged = [...state.activities];
          for (const row of activities) {
            if (row._id && !seen.has(row._id)) {
              merged.push(row);
              seen.add(row._id);
            }
          }
          state.activities = merged;
        }

        state.pagination = {
          currentPage,
          totalPages,
          total,
          hasNext: currentPage < totalPages,
          hasPrev: currentPage > 1,
        };
      })
      .addCase(fetchUserActivities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteActivityById.fulfilled, (state, action) => {
        const activityId = String(action.payload);
        const removed = state.activities.find((a) => String(a._id) === activityId);
        state.activities = state.activities.filter((a) => String(a._id) !== activityId);
        state.pagination.total = Math.max(0, state.pagination.total - 1);
        if (removed && !removed.isRead) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      })
      .addCase(fetchActivitySummary.pending, (state) => {
        state.summaryLoading = true;
      })
      .addCase(fetchActivitySummary.fulfilled, (state, action) => {
        state.summaryLoading = false;
        const { unreadCount, preview } = action.payload || {};
        state.unreadCount = typeof unreadCount === "number" ? unreadCount : 0;
        state.summaryPreview = Array.isArray(preview) ? preview : [];
      })
      .addCase(fetchActivitySummary.rejected, (state) => {
        state.summaryLoading = false;
      })
      .addCase(markActivityRead.fulfilled, (state, action) => {
        const id = String(action.payload);
        const row = state.activities.find((a) => String(a._id) === id);
        if (row && !row.isRead) {
          row.isRead = true;
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      })
      .addCase(markAllActivitiesRead.pending, (state) => {
        state.markAllLoading = true;
      })
      .addCase(markAllActivitiesRead.fulfilled, (state) => {
        state.markAllLoading = false;
        state.activities = state.activities.map((a) => ({ ...a, isRead: true }));
        state.unreadCount = 0;
        state.summaryPreview = [];
      })
      .addCase(markAllActivitiesRead.rejected, (state) => {
        state.markAllLoading = false;
      });
  },
});

export const { clearActivities, prependActivity } = activitySlice.actions;

export default activitySlice.reducer;
